import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CreateRoleInput,
  PermissionLevel,
  RoleKey,
  UpdateRoleInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../../../lib/api-client';

export interface RoleSummary {
  readonly id: string;
  readonly slug: string;
  readonly key: RoleKey | null;
  readonly baseRoleKey: RoleKey | null;
  readonly label: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly version: number;
  readonly assignedUserCount: number;
  /** "Copied from X" for a copy; null for a standard role and for one started blank. */
  readonly copiedFromLabel: string | null;
}

export interface RoleCell {
  readonly objectKey: string;
  readonly level: PermissionLevel;
  readonly scope: string | null;
  readonly isReadOnly: boolean;
  readonly restrictedFields: readonly string[];
}

export interface RoleTransition {
  readonly transitionKey: string;
  readonly isAllowed: boolean;
  readonly requiresReason: boolean;
  readonly note: string | null;
}

export interface RoleDetail extends RoleSummary {
  readonly cells: readonly RoleCell[];
  readonly transitions: readonly RoleTransition[];
}

export const ROLES_KEY = ['roles'] as const;

/** `isEnabled` false for somebody who may not open People and roles: `/roles` would only refuse them (review R22). */
export function useRoles(isEnabled = true): UseQueryResult<readonly RoleSummary[], ApiError> {
  return useQuery<readonly RoleSummary[], ApiError>({
    queryKey: ROLES_KEY,
    queryFn: () => apiGet<readonly RoleSummary[]>('/roles'),
    enabled: isEnabled,
  });
}

export function roleKey(id: string | null): readonly unknown[] {
  return [...ROLES_KEY, id];
}

export function useRole(id: string | null): UseQueryResult<RoleDetail | null, ApiError> {
  return useQuery<RoleDetail | null, ApiError>({
    queryKey: roleKey(id),
    queryFn: () => (id ? apiGet<RoleDetail>(`/roles/${id}`) : Promise.resolve(null)),
    enabled: id !== null,
  });
}

/** "Compare all roles" (Admin redesign 2026-11-02): every live role with its cells, read only. Fetched when opened. */
export function useRoleMatrix(enabled: boolean): UseQueryResult<readonly RoleDetail[], ApiError> {
  return useQuery<readonly RoleDetail[], ApiError>({
    queryKey: [...ROLES_KEY, 'matrix'],
    queryFn: () => apiGet<readonly RoleDetail[]>('/roles/matrix'),
    enabled,
  });
}

/** "Copy a role" (ADR 0085, Admin redesign 2026-11-02): blank, a standard role by key, or any live role by id. */
export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation<RoleDetail, ApiError, CreateRoleInput>({
    mutationFn: (input) => apiPost<RoleDetail>('/roles', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useUpdateRole(id: string) {
  const queryClient = useQueryClient();
  return useMutation<RoleDetail, ApiError, UpdateRoleInput>({
    mutationFn: (input) => apiPatch<RoleDetail>(`/roles/${id}`, input),
    onSuccess: async (updated) => {
      // Seeded from the response so the screen does not blank while a refetch runs
      // (CLAUDE.md §5a rule 2), then invalidated so the list's counts catch up.
      queryClient.setQueryData(roleKey(id), updated);
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

/** After a 409 "changed while you were editing": fetch the role as it is now. */
export function useReloadRole(id: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: roleKey(id) });
}

/** A save refused because someone else saved the role first (409, the `version` check). */
export function isStaleWrite(error: ApiError | null): boolean {
  return error?.code === 'stale_write';
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiDelete<void>(`/roles/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}
